import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for mobile menu state management
 * 
 * Handles:
 * - Open/close state
 * - Outside click detection
 * - Escape key press
 * - Body scroll lock when menu is open
 * 
 * Industry Pattern: GitHub Mobile, Linear Mobile, Stripe Dashboard
 */
export const useMobileMenu = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Open menu
  const open = useCallback(() => {
    setIsOpen(true);
    // Lock body scroll when menu opens
    document.body.style.overflow = 'hidden';
  }, []);

  // Close menu
  const close = useCallback(() => {
    setIsOpen(false);
    // Restore body scroll when menu closes
    document.body.style.overflow = '';
  }, []);

  // Toggle menu
  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  // Handle escape key press
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, close]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return {
    isOpen,
    open,
    close,
    toggle
  };
};

